/**
 * Unified Authentication Hook
 *
 * Abstraction layer that consumes AuthContext to provide a consistent
 * authentication API regardless of the underlying provider (MSAL or Kinde).
 *
 * The AuthContext is populated by bridge components (MsalAuthBridge or
 * KindeAuthBridge) within AuthProvider. This architecture ensures that
 * provider-specific hooks are only called inside their required SDK context.
 *
 * During the Kinde migration, this hook enables dual-provider support
 * without requiring changes to consuming components.
 *
 * @module useAuth
 * @see {@link AuthProvider} for context population
 * @see {@link AuthContext} for context definition
 */

import { useCallback, useMemo } from 'react'
import posthog from 'posthog-js'

import { useAuthContext } from '@/common/components/AuthProvider/AuthContext'
import { AUTH_PROVIDERS, getAuthProvider } from '@/common/helpers/auth/authProviderDetection'
import { purgeAuthState } from '@/common/helpers/auth/purgeAuthState'
import { appInsights } from '@/common/utils/ApplicationInsights/ApplicationInsights'
import { getUserDtoFromKindeUser, getUserDtoFromMsalAccountInfo } from '@/common/utils/DataModel/DataModel'

// Re-export for convenience
export { AUTH_PROVIDERS }

/**
 * Unified authentication hook supporting both MSAL and Kinde providers.
 *
 * Features:
 * - Automatic provider detection via AuthContext
 * - Consistent API regardless of underlying provider
 * - Backward compatible with existing MSAL-based code
 * - Exposes all methods from the active provider's manager
 *
 * @returns {Object} Authentication state and methods:
 *   - isAuthenticated: boolean - Whether user is currently authenticated
 *   - isLoading: boolean - Whether auth state is being determined
 *   - user: Object|null - Unified user DTO (userId, emailAddress, name, firstName, lastName)
 *   - provider: string - Current auth provider ('B2C' or 'Kinde')
 *   - login: Function - Initiates login flow
 *   - logout: Function - Initiates logout flow
 *   - register: Function - Initiates registration flow
 *   - resetPassword: Function - Initiates password reset flow
 *   - getToken: Function - Gets access token (async, returns {token})
 *   - getPermission: Function - Checks permission (Kinde only, returns null for MSAL)
 *   - isInteractionInProgress: Function - Checks if interaction is in progress
 *   - ensureInitialized: Function - Ensures auth provider is initialized
 *   - getActiveAccount: Function - Gets active account in provider format
 *   - _manager: Object - Direct access to the active manager (escape hatch)
 */
export const useAuth = () => {
    const authContext = useAuthContext()
    const { isAuthenticated, isLoading, manager, provider, user: rawUser } = authContext

    const isMsal = provider === AUTH_PROVIDERS.MSAL

    /**
     * Unified user object in consistent DTO format.
     * Transforms provider-specific user to unified shape.
     */
    const user = useMemo(() => {
        if (isMsal) {
            return rawUser ? getUserDtoFromMsalAccountInfo(rawUser) : null
        }
        return rawUser ? getUserDtoFromKindeUser(rawUser) : null
    }, [isMsal, rawUser])

    /**
     * Unified login method.
     *
     * @param {Object} [options] - Login options (provider-specific)
     * @returns {Promise<void>}
     */
    const login = useCallback(
        async (options = {}) => {
            if (isMsal) {
                await manager.ensureInitialized()
                const { loginRequest } = await import('@/authConfig')
                await manager.msalInstance.loginRedirect({ ...loginRequest, ...options })
            } else {
                manager.login(options)
            }
        },
        [isMsal, manager]
    )

    /**
     * Unified logout method.
     * Clears App Insights user context and provider cache for session hygiene.
     *
     * @param {Object} [options] - Logout options (provider-specific)
     * @returns {Promise<void>}
     */
    const logout = useCallback(
        async (options = {}) => {
            if (appInsights) {
                appInsights.clearAuthenticatedUserContext()
            }

            // QM-3550: reset the PostHog person so the next user to log in on a shared
            // browser doesn't inherit this user's identity and flag evaluations.
            posthog.reset()

            // Purge all local auth state before the provider SDK redirects.
            // Safe for MSAL: logoutRedirect() reads the active account from
            // its in-memory instance (populated at init), not from localStorage.
            await purgeAuthState()

            if (isMsal) {
                await manager.ensureInitialized()
                await manager.msalInstance.logoutRedirect(options)
                return
            }

            // Kinde SDK takes { allSessions, redirectUrl }. Translate legacy MSAL
            // option names so callers don't silently lose their redirect override.
            const kindeOptions = {
                allSessions: true,
                ...(options.redirectUrl || options.postLogoutRedirectUri
                    ? { redirectUrl: options.redirectUrl ?? options.postLogoutRedirectUri }
                    : {}),
            }
            await manager.logout(kindeOptions)
        },
        [isMsal, manager]
    )

    /**
     * Unified registration method.
     *
     * @param {Object} [options] - Registration options (provider-specific)
     * @returns {Promise<void>}
     */
    const register = useCallback(
        async (options = {}) => {
            if (isMsal) {
                const { signUpRequest } = await import('@/authConfig')
                await manager.ensureInitialized()
                await manager.msalInstance.loginRedirect({ ...signUpRequest, ...options })
            } else {
                manager.register(options)
            }
        },
        [isMsal, manager]
    )

    /**
     * Unified password reset method.
     *
     * For MSAL (B2C): Redirects to Azure AD B2C password reset policy.
     * For Kinde: Forces the Kinde sign-in page, which has a built-in "Forgot password?" link.
     *
     * @param {Object} [options] - Password reset options (provider-specific)
     * @returns {Promise<void>}
     */
    const resetPassword = useCallback(
        async (options = {}) => {
            if (isMsal) {
                const { resetPasswordRequest } = await import('@/authConfig')
                await manager.ensureInitialized()
                await manager.msalInstance.loginRedirect({ ...resetPasswordRequest, ...options })
            } else {
                // Kinde handles password reset via hosted UI ("Forgot password?" on the sign-in page).
                // prompt=login is required: without it Kinde reuses the live session and redirects straight back.
                manager.login({ prompt: 'login', ...options })
            }
        },
        [isMsal, manager]
    )

    /**
     * Unified token acquisition method.
     *
     * @returns {Promise<{token: string|null}>}
     */
    const getToken = useCallback(async () => {
        if (isMsal) {
            const { getAccessToken } = await import('@/common/helpers/auth/getAccessToken')
            return getAccessToken()
        }
        const token = await manager.getAccessToken()
        return { token }
    }, [isMsal, manager])

    /**
     * Unified permission check.
     * Returns null for MSAL since it uses different authorization model.
     *
     * @param {string} permission - Permission key to check
     * @returns {Object|null} Permission result with isGranted boolean, or null for MSAL
     */
    const getPermission = useCallback(
        (permission) => {
            if (isMsal) {
                // MSAL uses idTokenClaims.extension_UserRole for authorization
                // Return null to indicate Kinde-specific feature
                return null
            }
            return manager.getPermission(permission)
        },
        [isMsal, manager]
    )

    return {
        // State
        isAuthenticated,
        isLoading,
        user,
        provider,

        // Actions
        login,
        logout,
        register,
        resetPassword,
        getToken,
        getPermission,

        // Compatibility (delegate to active manager)
        isInteractionInProgress: manager.isInteractionInProgress,
        ensureInitialized: manager.ensureInitialized,
        getActiveAccount: manager.getActiveAccount,

        // Escape hatch (for edge cases during migration)
        _manager: manager,
    }
}

/**
 * Get access token for use outside React components.
 *
 * Used by RTK Query prepareHeaders and other non-React contexts.
 * Detects provider and delegates to appropriate token acquisition.
 *
 * @returns {Promise<{token: string|null}>} Access token
 */
export const getUnifiedAccessToken = async () => {
    const provider = getAuthProvider()

    if (provider === AUTH_PROVIDERS.MSAL) {
        const { getAccessToken } = await import('@/common/helpers/auth/getAccessToken')
        return getAccessToken()
    }

    // Use getRawToken() from Kinde SDK for non-React access
    try {
        const { getRawToken } = await import('@kinde-oss/kinde-auth-react/utils')
        const token = await getRawToken()
        return { token }
    } catch (error) {
        console.error('[getUnifiedAccessToken] Failed to get Kinde token:', error)
        return { token: null }
    }
}
